import { Collision } from '@/features/collisions/types';
import { ScheduleAlert } from '@/features/scheduleAlerts/types';
import { isEqual } from '@/helpers/utils/objects';

export const getScheduleAlertsForDate = (
  scheduleAlerts: Map<string, ScheduleAlert>,
  date: Date,
) => {
  const alertsForDate: ScheduleAlert[] = [];
  scheduleAlerts.forEach((alert) => {
    if (isEqual(alert.date, date)) {
      alertsForDate.push(alert);
    }
  });
  return alertsForDate;
};

export const isCollision = (alert?: ScheduleAlert): alert is Collision => {
  return Boolean(alert && 'dependencyId' in alert && alert.dependencyId);
};

export const findFirstScheduleAlertPerDay = (
  alertMap: Map<string, ScheduleAlert>,
): Map<string, ScheduleAlert> => {
  const getDateKey = (alert: ScheduleAlert): string => alert.date.toISOString();

  const alreadyFoundKeys = new Set<string>();
  const uniqueAlerts = new Map<string, ScheduleAlert>();

  alertMap.forEach((alert) => {
    const key = getDateKey(alert);
    if (alreadyFoundKeys.has(key)) return;

    alreadyFoundKeys.add(key);
    uniqueAlerts.set(alert.id, alert);
  });

  return uniqueAlerts;
};

export const findFirstScheduleAlertPerDayAndSectionAsCollision = (
  alertMap: Map<string, ScheduleAlert>,
): Map<string, Collision> => {
  const getDateKey = (collision: Collision): string =>
    `${collision.date.toISOString()}_${collision.wbsSectionId}`;

  const alreadyFoundKeys = new Set<string>();
  const uniqueCollisions = new Map<string, Collision>();

  alertMap.forEach((alert) => {
    if (!isCollision(alert)) return;

    const collision = alert;
    const key = getDateKey(collision);
    if (alreadyFoundKeys.has(key)) return;

    alreadyFoundKeys.add(key);
    uniqueCollisions.set(collision.id, collision);
  });

  return uniqueCollisions;
};

export const getScheduleAlertsOnDate = (
  scheduleAlerts: Map<string, ScheduleAlert>,
  date: Date,
): Map<string, ScheduleAlert> => {
  const alertsOnDate: Map<string, ScheduleAlert> = new Map();
  scheduleAlerts.forEach((alert) => {
    if (isEqual(alert.date, date)) {
      alertsOnDate.set(alert.id, alert);
    }
  });
  return alertsOnDate;
};

export const getScheduleAlertsByTarget = (
  scheduleAlerts: Map<string, ScheduleAlert>,
): Map<string, ScheduleAlert[]> => {
  const scheduleAlertsByTarget = new Map<string, ScheduleAlert[]>();
  scheduleAlerts.forEach((scheduleAlert) => {
    const key = isCollision(scheduleAlert) ? scheduleAlert.toId : 'collisions';
    const existing = scheduleAlertsByTarget.get(key);
    if (existing) {
      existing.push(scheduleAlert);
    } else {
      scheduleAlertsByTarget.set(key, [scheduleAlert]);
    }
  });
  return scheduleAlertsByTarget;
};
